import clsx from 'clsx';
import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { TwoKeySpinner } from './index';
import { colors } from '../../styles/_vars';

const ButtonComponent = ({
  className, children, loading,
  minWidth, loaderTheme, CustomLoader,
  ...rest
}) => (
  <button {...rest} className={clsx(className, { button__loading: loading })} data-min-width={minWidth}>
    {CustomLoader ? <CustomLoader loading={loading} /> : <TwoKeySpinner {...loaderTheme} />}
    <div className="button__children">
      {children}
    </div>
  </button>
);

ButtonComponent.propTypes = {
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  loading: PropTypes.bool,
  loaderTheme: PropTypes.object,
  CustomLoader: PropTypes.func,
};

ButtonComponent.defaultProps = {
  loading: false,
  minWidth: undefined,
  loaderTheme: {},
};

const BaseButton = styled(ButtonComponent)`
  position: relative;
  min-width: ${
  ({ minWidth }) => {
    if (minWidth === undefined) {
      return undefined;
    }

    return typeof minWidth === 'string' ? minWidth : `${minWidth}px`;
  }
};
  padding: 3px 14px;
  min-height: 35px;
  border-radius: 2px;
  transition: 0.2s all;
  border: solid 2px transparent;
  text-align: center;
  font-size: 16px;

  &:disabled{
    opacity: 0.65;
  }
  
  &:focus {
    outline: none;
  }
  
  .lds-default{
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    visibility: hidden;
    max-height: 100%;
    height: 40px;
    transition: visibility .2s;
  }
  
  &.button__loading{
    .button__children{
      visibility: hidden;
    }
    
    .lds-default{
      visibility: visible;
    }
  }
`;

const BaseTextButton = styled.button`
  padding: 0;
  min-height: 0;
  min-width: 0;
  border: none;
  cursor: pointer;
  background: none;
  text-decoration: underline;
  
  &, &:focus{
    outline: none;
  }
  
  &:disabled{
    opacity: 0.8;
    cursor: default;
  }
`;

export const PrimaryButton = styled(BaseButton)`
  &:not(:disabled){
    border-color: #17936F;
    background: #17936F;
    color: #ffffff;
    
    &:hover{
      background: #00B480;
      border-color: #00B480;
    }
  }
  
  &:disabled{
    background-color: #707070;
    border-color: rgba(112,112,112,0.65);
    color: rgba(255, 255, 255, 0.4);
    cursor: not-allowed;
  }
`;

export const PrimaryGradientButton = styled(BaseButton)`
  &:not(:disabled){
    min-height: 27px;
    padding: 4px 22px;
    border: 0;
    border-radius: 7px;
    background: transparent linear-gradient(180deg, #00b480 0%, #005a40 100%) 0 0 no-repeat padding-box;
    color: #ffffff;
    box-shadow: 0 3px 6px #00000029;
    font-size: 14px;
    
    &:hover{
      border-color: #00B480;
      background: transparent linear-gradient(0deg, #00b480 0%, #005a40 100%) 0 0 no-repeat padding-box;
    }
  }
  
  &:disabled{
    background-color: #707070;
    border-color: rgba(112,112,112,0.65);
    color: rgba(255, 255, 255, 0.4);
    cursor: not-allowed;
  }
  
  .lds-default {
    width: 20px;
    height: 19px;
  }
`;

export const PrimaryButtonLoader = styled(PrimaryButton)`
  &.button__loading {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  
    .button__children { visibility: visible; }
    
    .lds-default {
      width: 30px;
      height: 27px;
      margin-right: 5px;
      transform: none;
      position: static;
      visibility: visible;
    }
  }
`;

export const PrimaryRoundButton = styled(PrimaryButtonLoader)`
  padding: 8px 15px;
  font-size: 14px;
  min-height: auto;
  
  &:not(.disabled) {
    background: none;
    border: 1px solid ${colors.grayDark};
    border-radius: 30px;
    color: ${colors.grayDarker};
    
    &.success {
      border: none;
      color: #fff;
      background: ${colors.green};

      &:hover { background: ${colors.greenHover}; }
    }
    
    &:hover {
      background: ${colors.grayLight3};
      border-color: ${colors.grayDarker};
    }
    
    &.next {
      border: none;
      color: ${colors.white};
      background: ${colors.green3};
      min-height: 36px;

      display: flex;
      justify-content: center;
      align-items: center;
      

      &:hover { background: ${colors.green4}; }
    }
  }
  
  &:disabled {
    background: ${colors.grayDarker};
    border-color: ${colors.grayDarker};
    color: #fff;
    
    &:hover, &.success, &.success:hover, &.next, &.next:hover { background: ${colors.grayDarker}; }
  }
  
  &.button__loading {
    padding-top: 4px;
    padding-bottom: 4px;
    
    .button__children { display: none; }
  }
  
  .twokey-spinner { margin-right: 0; }
`;

export const PrimaryInvertedButton = styled(BaseButton)`
  &:not(:disabled){
    border-color: #fff;
    background: #fff;
    color: #17936f;
    
    &:hover{
      background: #ccc;
      border-color: #ccc;
    }
  }
  
  &:disabled{
    background-color: #707070;
    border-color: rgba(112,112,112,0.65);
    color: rgba(255, 255, 255, 0.4);
    cursor: default;
  }
`;

export const PrimaryOutlineButton = styled(BaseButton)`
  &:not(:disabled){
    border-color: #17936F;
    background: #ffffff;
    color: #17936F;
    
    &:hover{
      border-color: #00B480;
      color: #00B480;
    }
  }
  
  &:disabled{
    border-color: rgba(112,112,112,0.65);
    color: rgba(112,112,112,0.65);
    cursor: default;
  }
`;

export const PrimaryTextButton = styled(BaseTextButton)`
  color: #17936F;

  &:hover{
    color: #00B480;
  }
`;

export const GrayOutlineButton = styled(BaseButton)`
  &:not(:disabled){
    border-color: #CCCCCC;
    background: #ffffff;
    color: #CCCCCC;
    
    &:hover{
      background: rgba(204,204,204,0.1); // #CCCCCC with opacity
    }
  }
  
  &:disabled{
    opacity: 0.8;
    cursor: not-allowed;
  }
`;

export const GrayTextButton = styled(BaseTextButton)`
  color: #999999;

  &:hover{
    color: #898989;
  }
  
  &.link {
    font: normal normal normal 12px/24px Roboto;
    letter-spacing: 0.22px;
    color: ${colors.gray};
    
    &:hover {
      color: ${colors.grayDark}
    }

    &:disabled {
      color: ${colors.grayDark};
      cursor: not-allowed;
    }
  }
`;

export default {
  PrimaryButton,
  PrimaryOutlineButton,
  GrayOutlineButton,
  GrayTextButton,
};
